import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Dispatch, MouseEvent, SetStateAction } from "react";
import type { RiskFlowVisualCandidate } from "../../lib/riskflow-visual-enrichment";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

interface RiskFlowTickerCyclerProps {
  visualCandidates: RiskFlowVisualCandidate[];
  visualIndex: number;
  onVisualIndexChange: Dispatch<SetStateAction<number>>;
}

export function RiskFlowTickerCycler({
  visualCandidates,
  visualIndex,
  onVisualIndexChange,
}: RiskFlowTickerCyclerProps) {
  const tickers = uniqueTickers(visualCandidates);
  const activeVisual = visualCandidates[visualIndex] ?? null;
  const activeTicker = activeVisual?.ticker;
  if (!activeTicker || tickers.length < 2) return null;

  const activeTickerIndex = Math.max(0, tickers.indexOf(activeTicker));
  const changeTicker = (
    direction: -1 | 1,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const nextTicker =
      tickers[
        (activeTickerIndex + direction + tickers.length) % tickers.length
      ];
    const sameSourceIndex = visualCandidates.findIndex(
      (candidate) =>
        candidate.ticker === nextTicker &&
        candidate.source === activeVisual.source,
    );
    const nextIndex =
      sameSourceIndex >= 0
        ? sameSourceIndex
        : visualCandidates.findIndex(
            (candidate) => candidate.ticker === nextTicker,
          );
    if (nextIndex >= 0) onVisualIndexChange(nextIndex);
  };

  return (
    <div
      className="pointer-events-auto absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5"
      data-riskflow-ticker-cycler
    >
      <button
        type="button"
        onClick={(event) => changeTicker(-1, event)}
        className="solvys-liquid-glass fintheon-black-liquid-glass flex h-7 w-7 items-center justify-center rounded-full text-[var(--fintheon-text)]/80 transition-[color,text-shadow] hover:text-[var(--fintheon-accent)] hover:[text-shadow:0_0_10px_rgba(199,159,74,0.45)] focus:outline-none focus-visible:text-[var(--fintheon-accent)]"
        aria-label={`Show previous ticker (${tickers.length} mentioned)`}
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className="solvys-liquid-glass fintheon-black-liquid-glass flex h-7 min-w-[72px] items-center justify-center rounded-full px-2.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/75 tabular-nums">
        {activeTicker} <FintheonNumericText text={activeTickerIndex + 1} />/
        <FintheonNumericText text={tickers.length} kind="count" />
      </span>
      <button
        type="button"
        onClick={(event) => changeTicker(1, event)}
        className="solvys-liquid-glass fintheon-black-liquid-glass flex h-7 w-7 items-center justify-center rounded-full text-[var(--fintheon-text)]/80 transition-[color,text-shadow] hover:text-[var(--fintheon-accent)] hover:[text-shadow:0_0_10px_rgba(199,159,74,0.45)] focus:outline-none focus-visible:text-[var(--fintheon-accent)]"
        aria-label={`Show next ticker (${tickers.length} mentioned)`}
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function uniqueTickers(candidates: RiskFlowVisualCandidate[]): string[] {
  const seen = new Set<string>();
  const tickers: string[] = [];
  for (const candidate of candidates) {
    if (!candidate.ticker || seen.has(candidate.ticker)) continue;
    seen.add(candidate.ticker);
    tickers.push(candidate.ticker);
  }
  return tickers;
}
